import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const StoreTable = ({ stores, onStoreSelect, selectedStore, onEditStore, onDeleteStore }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [rentFilter, setRentFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const locationOptions = [
    { value: '', label: 'All Locations' },
    { value: 'dhaka', label: 'Dhaka' },
    { value: 'chittagong', label: 'Chittagong' },
    { value: 'sylhet', label: 'Sylhet' },
    { value: 'rajshahi', label: 'Rajshahi' },
    { value: 'khulna', label: 'Khulna' }
  ];

  const sizeOptions = [
    { value: '', label: 'All Sizes' },
    { value: '0-500', label: '0-500 sq ft' },
    { value: '500-1000', label: '500-1000 sq ft' },
    { value: '1000-2000', label: '1000-2000 sq ft' },
    { value: '2000+', label: '2000+ sq ft' }
  ];

  const rentOptions = [
    { value: '', label: 'All Rent Ranges' },
    { value: '0-50000', label: '৳0 - ৳50,000' },
    { value: '50000-100000', label: '৳50,000 - ৳1,00,000' },
    { value: '100000-200000', label: '৳1,00,000 - ৳2,00,000' },
    { value: '200000+', label: '৳2,00,000+' }
  ];

  const filteredStores = stores?.filter(store => {
    const matchesSearch = store?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         store?.location?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const matchesLocation = !locationFilter || store?.location?.toLowerCase() === locationFilter;
    
    const matchesSize = !sizeFilter || (() => {
      const size = store?.size;
      switch (sizeFilter) {
        case '0-500': return size <= 500;
        case '500-1000': return size > 500 && size <= 1000;
        case '1000-2000': return size > 1000 && size <= 2000;
        case '2000+': return size > 2000;
        default: return true;
      }
    })();
    
    const matchesRent = !rentFilter || (() => {
      const rent = store?.monthlyRent;
      switch (rentFilter) {
        case '0-50000': return rent <= 50000;
        case '50000-100000': return rent > 50000 && rent <= 100000;
        case '100000-200000': return rent > 100000 && rent <= 200000;
        case '200000+': return rent > 200000;
        default: return true;
      }
    })();
    
    return matchesSearch && matchesLocation && matchesSize && matchesRent;
  });

  const sortedStores = [...filteredStores]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];
    
    if (sortConfig?.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('bn-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0
    })?.format(amount)?.replace('BDT', '৳');
  };

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Store Directory</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage store information and calculate branding costs
            </p>
          </div>
          <div className="text-sm text-muted-foreground">
            {filteredStores?.length} of {stores?.length} stores
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="p-6 border-b border-border bg-muted/30">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            type="search"
            placeholder="Search stores..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full"
          />
          <Select
            placeholder="Filter by location"
            options={locationOptions}
            value={locationFilter}
            onChange={setLocationFilter}
          />
          <Select
            placeholder="Filter by size"
            options={sizeOptions}
            value={sizeFilter}
            onChange={setSizeFilter}
          />
          <Select
            placeholder="Filter by rent"
            options={rentOptions}
            value={rentFilter}
            onChange={setRentFilter}
          />
        </div>
        
        {(searchQuery || locationFilter || sizeFilter || rentFilter) && (
          <div className="flex items-center gap-2 mt-4">
            <span className="text-sm text-muted-foreground">Active filters:</span>
            <div className="flex flex-wrap gap-2">
              {searchQuery && (
                <div className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded text-xs">
                  Search: {searchQuery}
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setSearchQuery('')}
                    className="h-4 w-4 p-0 hover:bg-primary/20"
                  >
                    <Icon name="X" size={12} />
                  </Button>
                </div>
              )}
              {locationFilter && (
                <div className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded text-xs">
                  Location: {locationOptions?.find(opt => opt?.value === locationFilter)?.label}
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setLocationFilter('')}
                    className="h-4 w-4 p-0 hover:bg-primary/20"
                  >
                    <Icon name="X" size={12} />
                  </Button>
                </div>
              )}
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  setSearchQuery('');
                  setLocationFilter('');
                  setSizeFilter('');
                  setRentFilter('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                Clear all
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('name')}
                  className="gap-2 font-medium text-muted-foreground hover:text-foreground"
                >
                  Store Name
                  <Icon name={getSortIcon('name')} size={14} />
                </Button>
              </th>
              <th className="text-left p-4 font-medium text-muted-foreground">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('location')}
                  className="gap-2 font-medium text-muted-foreground hover:text-foreground"
                >
                  Location
                  <Icon name={getSortIcon('location')} size={14} />
                </Button>
              </th>
              <th className="text-right p-4 font-medium text-muted-foreground">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('size')}
                  className="gap-2 font-medium text-muted-foreground hover:text-foreground"
                >
                  Size (sq ft)
                  <Icon name={getSortIcon('size')} size={14} />
                </Button>
              </th>
              <th className="text-right p-4 font-medium text-muted-foreground">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('monthlyRent')}
                  className="gap-2 font-medium text-muted-foreground hover:text-foreground"
                >
                  Monthly Rent
                  <Icon name={getSortIcon('monthlyRent')} size={14} />
                </Button>
              </th>
              <th className="text-right p-4 font-medium text-muted-foreground">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('rentPerSqFt')}
                  className="gap-2 font-medium text-muted-foreground hover:text-foreground"
                >
                  Rent/sq ft
                  <Icon name={getSortIcon('rentPerSqFt')} size={14} />
                </Button>
              </th>
              <th className="text-center p-4 font-medium text-muted-foreground">Layout Status</th>
              <th className="text-center p-4 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedStores?.map((store) => (
              <tr
                key={store?.id}
                className={`border-t border-border hover:bg-muted/30 cursor-pointer transition-colors ${
                  selectedStore?.id === store?.id ? 'bg-primary/5 border-primary/20' : ''
                }`}
                onClick={() => onStoreSelect(store)}
              >
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Store" size={20} className="text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{store?.name}</div>
                      <div className="text-sm text-muted-foreground">ID: {store?.id}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={16} className="text-muted-foreground" />
                    <span className="text-foreground">{store?.location}</span>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <span className="font-medium text-foreground">
                    {store?.size?.toLocaleString('bn-BD')}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <span className="font-medium text-foreground">
                    {formatCurrency(store?.monthlyRent)}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <span className="font-medium text-accent">
                    {formatCurrency(store?.rentPerSqFt)}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    store?.hasCurrentLayout 
                      ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                  }`}>
                    <Icon 
                      name={store?.hasCurrentLayout ? 'CheckCircle' : 'AlertCircle'} 
                      size={12} 
                    />
                    {store?.hasCurrentLayout ? 'Current' : 'Pending'}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e?.stopPropagation();
                        onEditStore(store);
                      }}
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e?.stopPropagation();
                        onDeleteStore(store);
                      }}
                      className="h-8 w-8 text-muted-foreground hover:text-error"
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden">
        {sortedStores?.map((store) => (
          <div
            key={store?.id}
            className={`p-4 border-b border-border cursor-pointer hover:bg-muted/30 transition-colors ${
              selectedStore?.id === store?.id ? 'bg-primary/5 border-primary/20' : ''
            }`}
            onClick={() => onStoreSelect(store)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Store" size={20} className="text-primary" />
                </div>
                <div>
                  <div className="font-medium text-foreground">{store?.name}</div>
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <Icon name="MapPin" size={12} />
                    {store?.location}
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e?.stopPropagation();
                    onEditStore(store);
                  }}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                >
                  <Icon name="Edit" size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e?.stopPropagation();
                    onDeleteStore(store);
                  }}
                  className="h-8 w-8 text-muted-foreground hover:text-error"
                >
                  <Icon name="Trash2" size={16} />
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Size:</span>
                <span className="ml-2 font-medium text-foreground">
                  {store?.size?.toLocaleString('bn-BD')} sq ft
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Monthly Rent:</span>
                <span className="ml-2 font-medium text-foreground">
                  {formatCurrency(store?.monthlyRent)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Rent/sq ft:</span>
                <span className="ml-2 font-medium text-accent">
                  {formatCurrency(store?.rentPerSqFt)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Layout:</span>
                <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                  store?.hasCurrentLayout 
                    ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                }`}>
                  <Icon 
                    name={store?.hasCurrentLayout ? 'CheckCircle' : 'AlertCircle'} 
                    size={10} 
                  />
                  {store?.hasCurrentLayout ? 'Current' : 'Pending'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {sortedStores?.length === 0 && (
        <div className="p-12 text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Search" size={24} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No stores found</h3>
          <p className="text-muted-foreground mb-4">
            Try adjusting your search criteria or add a new store.
          </p>
          <Button variant="outline" onClick={() => {
            setSearchQuery('');
            setLocationFilter('');
            setSizeFilter('');
            setRentFilter('');
          }}>
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default StoreTable;